import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Layout, Row, Select, Space, Button, Table, Typography, Switch } from 'antd';
import { sessionStore, SESSION_ATTRS, ACTIONS } from '../store/session';
import { getBuildingTenants } from '../utils/common-utils';
import ConfirmationModal from '../components/ConfirmationModal';
import { notifySuccess } from '../utils/notification';
import { updateFeature } from './actions';
import { APIS, getApi } from '../utils/api-utils';
import { isTenantVisitorPassDelegationEnabled } from '../utils/tenantFeatures';

const { Content } = Layout;
const { Option } = Select;
const { Title } = Typography;

const ALL = "all";
const VISITOR_WALLET_FEATURE = 'visitor-wallet-pass-delegation';

interface Feature {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  tenantId?: string;
}


const DEFAULT_FEATURES: Feature[] = [
    {
      id: 'inbox',
      name: 'Inbox',
      description: 'Inbox feature for users (Display Newsfeed & Issues)',
      enabled: true
    },
    {
      id: 'induction',
      name: 'Videos',
      description: 'videos feature',
      enabled: true
    },
    {
      id: 'issues',
      name: 'Issues',
      description: 'Issues reporting feature',
      enabled: true
    },
    {
      id: 'space-booking',
      name: 'Space Booking',
      description: 'Space booking feature for users',
      enabled: true
    },
    {
      id: 'building-contacts',
      name: 'Building Contacts',
      description: 'Building contacts feature for mobile app',
      enabled: true
    },
    {
      id: 'visitor-wallet-pass-delegation',
      name: 'Visitor Access Pass',
      description: 'Allow mobile app users to create temporary visitor Wallet passes',
      enabled: true
    }
];

export const FeatureManagementView = () => {
  const [features] = useState<Feature[]>(DEFAULT_FEATURES);

  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    feature: Feature | null;
    action: 'enable' | 'disable';
  }>({
    visible: false,
    feature: null,
    action: 'enable'
  });

  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [filteredFeatures, setFilteredFeatures] = useState<Feature[]>(features);

  const { state, dispatch } = useContext(sessionStore);
  const tenants: any = state.get(SESSION_ATTRS.TENANTS);
  const loggedUser: any = state.get(SESSION_ATTRS.LOGGED_USER);

  // Function to reload tenants
  const reloadTenants = useCallback(async () => {
    if (loggedUser && loggedUser.buildingId) {
      try {
        const tenantsData = await getApi(APIS.TENANT_API, `/tenants/${loggedUser.buildingId}`);
        dispatch({
          type: ACTIONS.TENANTS_LOADED,
          payload: tenantsData
        });
      } catch (error) {
        console.error('Error reloading tenants:', error);
      }
    }
  }, [loggedUser, dispatch]);

  useEffect(() => {
    reloadTenants();
  }, [reloadTenants]);

  // Update features based on selected tenant's disabledFeatures
  useEffect(() => {
    if (selectedTenantId && tenants) {
      const selectedTenant = tenants.find((t: any) => t.tenantId === selectedTenantId);
      const visibleFeatures = isTenantVisitorPassDelegationEnabled(selectedTenant)
        ? features
        : features.filter(feature => feature.id !== VISITOR_WALLET_FEATURE);
      if (selectedTenant && selectedTenant.disabledFeatures) {
        const disabledFeatures = selectedTenant.disabledFeatures;
        const updatedFeatures = visibleFeatures.map(feature => ({
          ...feature,
          enabled: !disabledFeatures.includes(feature.id)
        }));
        console.log('Updated Features:', updatedFeatures);
        setFilteredFeatures(updatedFeatures);
      } else {
        // If no disabledFeatures array, all features are enabled
        setFilteredFeatures(visibleFeatures.map(feature => ({ ...feature, enabled: true })));
      }
    } else {
      setFilteredFeatures([]);
    }
  }, [selectedTenantId, tenants, features]);

  const handleToggle = (feature: Feature, checked: boolean) => {
    setConfirmModal({
      visible: true,
      feature,
      action: checked ? 'enable' : 'disable'
    });
  };

  const handleConfirm = async () => {
    if (confirmModal.feature && selectedTenantId) {
      setIsUpdating(true);
      try {
        // Call the API to update the feature
        await updateFeature(
          selectedTenantId,
          confirmModal.feature.id,
          confirmModal.action === 'enable'
        );
        
        // Update the tenant's disabledFeatures array locally
        const selectedTenant = tenants.find((t: any) => t.tenantId === selectedTenantId);
        if (selectedTenant) {
          const disabledFeatures = selectedTenant.disabledFeatures || [];
          let updatedDisabledFeatures;
          
          if (confirmModal.action === 'disable') {
            // Add feature to disabledFeatures if not already there
            updatedDisabledFeatures = disabledFeatures.includes(confirmModal.feature.id)
              ? disabledFeatures
              : [...disabledFeatures, confirmModal.feature.id];
          } else {
            // Remove feature from disabledFeatures
            updatedDisabledFeatures = disabledFeatures.filter((f: string) => f !== confirmModal.feature!.id);
          }
          
          // Update the tenant in the tenants array
          const updatedTenants = tenants.map((t: any) =>
            t.tenantId === selectedTenantId
              ? { ...t, disabledFeatures: updatedDisabledFeatures }
              : t
          );
          
          // Update session state with TENANTS_LOADED action
          dispatch({
            type: ACTIONS.TENANTS_LOADED,
            payload: updatedTenants
          });
          
          // Update local features state
          const updatedFeatures = filteredFeatures.map(f =>
            f.id === confirmModal.feature!.id
              ? { ...f, enabled: confirmModal.action === 'enable' }
              : f
          );
          setFilteredFeatures(updatedFeatures);
          
          notifySuccess(
            `${confirmModal.feature.name} feature ${confirmModal.action === 'enable' ? 'enabled' : 'disabled'} successfully`
          );
        }
      } catch (error) {
        console.error('Error updating feature:', error);
        // Handle error appropriately (could show error notification)
      } finally {
        setIsUpdating(false);
        setConfirmModal({ visible: false, feature: null, action: 'enable' });
      }
    }
  };

  const handleCancel = () => {
    setConfirmModal({ visible: false, feature: null, action: 'enable' });
  };

  const onTenantChange = (value: string) => {
    setSelectedTenantId(value);
  };

  const statusMapper = (enabled: boolean) => {
    return enabled ? <span className="status status--active"> Enabled </span> : <span className="status status--inactive"> Disabled </span>;
  };

  const columns: Array<any> = [
    {
      title: 'Feature',
      dataIndex: 'name',
      render: (name: string, record: Feature) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{name}</div>
          <div style={{ color: '#666', fontSize: '12px' }}>{record.description}</div>
        </div>
      ),
      sorter: (a: any, b: any) => (a.name || '').localeCompare(b.name || '')
    },
    {
      title: 'Status',
      dataIndex: 'enabled',
      render: (enabled: boolean) => statusMapper(enabled),
      sorter: (a: any, b: any) => a.enabled === b.enabled ? 0 : (a.enabled ? 1 : -1)
    },
    {
      title: 'Tenant',
      dataIndex: 'tenantId',
      render: (tenantId: string) => {
        return selectedTenantId ? (tenants.find((t: any) => t.tenantId === selectedTenantId)?.name || 'Unknown') : 'Not Selected';
      }
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'id',
      render: (id: string, record: Feature) => (
        <Switch
          checked={record.enabled}
          onChange={(checked: boolean) => handleToggle(record, checked)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      )
    }
  ];

  return (
    <>
      <Space direction='vertical'>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Row style={{ marginTop: 15 }}>
            <Title level={4}>
              Feature Management
            </Title>
          </Row>
          <Row className="grid-options">
            <Space style={{ marginBottom: 16, flexWrap: "wrap" }}>
              <Select value={selectedTenantId || undefined} onChange={onTenantChange} style={{ width: 150 }} placeholder="Select Tenant">
                {tenants.map((tenant: any) => (
                  <Option key={tenant.tenantId} value={tenant.tenantId}>{tenant.name}</Option>
                ))}
              </Select>
            </Space>
          </Row>
        </Space>
        <Content className="table-wrapper">
          <Table
            columns={columns}
            dataSource={selectedTenantId ? filteredFeatures : []}
            rowKey='id'
            locale={{
              emptyText: selectedTenantId ? 'No features available' : 'Please select a tenant to view feature management options'
            }}
            pagination={selectedTenantId ? {
              total: filteredFeatures.length,
              showSizeChanger: true,
              showTotal: (total: number, range: number[]) => `showing ${range[0]}-${range[1]} out of ${total} features`
            } : false}
          />
        </Content>
      </Space>

      <ConfirmationModal
        visible={confirmModal.visible}
        title={`${confirmModal.action === 'enable' ? 'Enable' : 'Disable'} Feature`}
        message={`Are you sure you want to ${confirmModal.action} the "${confirmModal.feature?.name}" feature?`}
        confirmText={confirmModal.action === 'enable' ? 'Enable' : 'Disable'}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isDanger={confirmModal.action === 'disable'}
        confirmLoading={isUpdating}
      />
    </>
  );
};
